import { RefObject, useEffect } from 'react';
import type { Feature } from 'geojson';

import {
  DRAG_THRESHOLD_PX,
  DRAWING_Z_INDEX,
  OUTLINE_COLOR,
  STROKE_WEIGHT,
} from '../drawingConstants';
import { CommittedShape } from '../drawingUtilities';
import { DrawingMode } from '../geometry';

type UseCircleDrawArgs = {
  map: google.maps.Map | null;
  // Armed to draw a circle (mode is 'circle' and no shape exists yet).
  enabled: boolean;
  projectionOverlayRef: RefObject<google.maps.OverlayView | null>;
  committedRef: RefObject<CommittedShape | null>;
  makeEditable: (shape: CommittedShape['shape'], kind: DrawingMode) => void;
  overlayToFeature: () => Feature | null;
  commitNow: (feature: Feature) => void;
};

// Circle draw via press-drag (down, drag out radius, release) OR two-click
// (click centre, click radius). Adopts the finished circle as the committed,
// editable shape.
const useCircleDraw = ({
  map,
  enabled,
  projectionOverlayRef,
  committedRef,
  makeEditable,
  overlayToFeature,
  commitNow,
}: UseCircleDrawArgs) => {
  useEffect(() => {
    if (!map || !enabled) {
      return undefined;
    }

    const div = map.getDiv();
    const savedDraggable =
      (map.get('draggable') as boolean | undefined) ?? true;
    // Suppress panning for the whole armed window
    map.setOptions({ draggable: false });

    const toLatLng = (clientX: number, clientY: number) => {
      const projection = projectionOverlayRef.current?.getProjection();
      if (!projection) {
        return null;
      }

      const rect = div.getBoundingClientRect();
      return projection.fromContainerPixelToLatLng(
        new google.maps.Point(clientX - rect.left, clientY - rect.top),
      );
    };

    let center: google.maps.LatLng | null = null;
    let circle: google.maps.Circle | null = null;
    let pressing = false; // pointer is currently down
    let movedSincePress = false; // pointer moved past the drag threshold
    let awaitingRadiusClick = false; // first click placed; waiting for second
    let pressX = 0;
    let pressY = 0;

    const radiusTo = (point: google.maps.LatLng) =>
      center
        ? google.maps.geometry.spherical.computeDistanceBetween(center, point)
        : 0;

    const reset = () => {
      center = null;
      circle = null;
      pressing = false;
      movedSincePress = false;
      awaitingRadiusClick = false;
    };

    const commit = () => {
      if (circle && circle.getRadius() > 0) {
        committedRef.current = { kind: 'circle', shape: circle };
        makeEditable(circle, 'circle');
        const feature = overlayToFeature();
        if (feature) {
          commitNow(feature);
        }
      } else {
        circle?.setMap(null);
      }
      reset();
    };

    const onPointerDown = (event: PointerEvent) => {
      const point = toLatLng(event.clientX, event.clientY);
      if (!point) {
        return;
      }
      // Second click of a two-click draw: lock in the radius and commit.
      if (awaitingRadiusClick && center && circle) {
        circle.setRadius(radiusTo(point));
        commit();
        return;
      }
      // First press: place the centre (works for both drag and two-click).
      center = point;
      pressing = true;
      movedSincePress = false;
      pressX = event.clientX;
      pressY = event.clientY;
      circle = new google.maps.Circle({
        map,
        center: point,
        radius: 0,
        fillOpacity: 0,
        strokeColor: OUTLINE_COLOR,
        strokeWeight: STROKE_WEIGHT,
        clickable: false,
        zIndex: DRAWING_Z_INDEX,
      });
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!center || !circle || (!pressing && !awaitingRadiusClick)) {
        return;
      }

      if (
        pressing &&
        !movedSincePress &&
        Math.hypot(event.clientX - pressX, event.clientY - pressY) >
          DRAG_THRESHOLD_PX
      ) {
        movedSincePress = true;
      }

      const current = toLatLng(event.clientX, event.clientY);
      if (current) {
        circle.setRadius(radiusTo(current));
      }
    };

    const onPointerUp = () => {
      if (!pressing) {
        return;
      }

      if (movedSincePress) {
        // Drag draw: the release sets the radius and commits.
        commit();
      } else {
        // A plain click: arm the two-click draw and wait for the radius click.
        pressing = false;
        awaitingRadiusClick = true;
      }
    };

    div.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      div.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      map.setOptions({ draggable: savedDraggable });
      // Discard any in-progress (uncommitted) circle.
      circle?.setMap(null);
    };
  }, [
    map,
    enabled,
    projectionOverlayRef,
    committedRef,
    makeEditable,
    overlayToFeature,
    commitNow,
  ]);
};

export default useCircleDraw;
